import { useEffect, useState, useCallback } from 'react'
import toast from 'react-hot-toast'
import ssoConfigService from '../../services/ssoConfigService'
import authService from '../../services/authService'
import userService, { ROLE_LABELS } from '../../services/userService'
import { HiOutlineTrash, HiOutlinePlus, HiOutlineIdentification, HiOutlineLink } from 'react-icons/hi'

function SsoConfigManagement() {
  const [roles, setRoles] = useState(['admin', 'hr_manager', 'team_lead', 'member'])
  const [maps, setMaps] = useState([])
  const [links, setLinks] = useState([])
  const [users, setUsers] = useState([])
  const [loading, setLoading] = useState(true)
  const [ssoEnabled, setSsoEnabled] = useState(null)

  // form thêm ánh xạ vai trò
  const [newRole, setNewRole] = useState('')
  const [newInternal, setNewInternal] = useState('member')
  // form thêm liên kết tài khoản
  const [newEmail, setNewEmail] = useState('')
  const [newUserId, setNewUserId] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const [rm, al, us] = await Promise.all([
        ssoConfigService.listRoleMaps(),
        ssoConfigService.listAccountLinks(),
        userService.list(),
      ])
      if (rm.status === 'success') { setMaps(rm.data.items || []); if (rm.data.roles) setRoles(rm.data.roles) }
      if (al.status === 'success') setLinks(al.data || [])
      if (us.status === 'success') setUsers(us.data || [])
    } finally { setLoading(false) }
  }, [])
  useEffect(() => { load() }, [load])
  useEffect(() => {
    authService.ssoStatus().then((r) => setSsoEnabled(!!r.data?.enabled)).catch(() => {})
  }, [])

  const addMap = async () => {
    if (!newRole.trim()) { toast.error('Nhập vai trò SSO'); return }
    try {
      await ssoConfigService.createRoleMap({ sso_role: newRole.trim(), internal_role: newInternal, is_active: true })
      toast.success('Đã thêm ánh xạ'); setNewRole(''); setNewInternal('member'); load()
    } catch { /* interceptor */ }
  }
  const changeMap = async (m, patch) => {
    try { await ssoConfigService.updateRoleMap(m.id, patch); load() } catch { /* */ }
  }
  const delMap = async (m) => {
    if (!window.confirm(`Xóa ánh xạ "${m.sso_role}"?`)) return
    try { await ssoConfigService.removeRoleMap(m.id); toast.success('Đã xóa'); load() } catch { /* */ }
  }

  const addLink = async () => {
    if (!newEmail.trim() || !newUserId) { toast.error('Nhập email công ty + chọn tài khoản'); return }
    try {
      await ssoConfigService.createAccountLink({ sso_email: newEmail.trim(), user_id: Number(newUserId) })
      toast.success('Đã liên kết'); setNewEmail(''); setNewUserId(''); load()
    } catch { /* */ }
  }
  const delLink = async (l) => {
    if (!window.confirm(`Gỡ liên kết "${l.sso_email}"?`)) return
    try { await ssoConfigService.removeAccountLink(l.id); toast.success('Đã gỡ'); load() } catch { /* */ }
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="page-title">Cấu hình đăng nhập SSO</h1>
        <p className="page-subtitle">Ánh xạ vai trò công ty → vai trò nội bộ, và liên kết tài khoản SSO với tài khoản có sẵn (phạm vi tuyển dụng).</p>
      </div>

      {ssoEnabled === false && (
        <div className="mb-6 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-800">
          SSO đang <b>TẮT</b> — trang đăng nhập chỉ dùng tài khoản nội bộ. Cấu hình ở đây được lưu sẵn, có hiệu lực khi bật
          <code className="mx-1 text-xs">SSO_ENABLED=True</code>(kèm <code className="text-xs">SSO_CLIENT_ID/SECRET/REDIRECT_URI</code>) trong <code className="text-xs">backend/.env</code>.
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner w-8 h-8" /></div>
      ) : (
        <div className="space-y-8">
          {/* Ánh xạ vai trò */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2 mb-1"><HiOutlineIdentification className="w-5 h-5 text-primary-600" /> Ánh xạ vai trò</h2>
            <p className="text-sm text-secondary-500 mb-4">Chỉ vai trò có trong bảng (đang bật) mới được đăng nhập. Vai trò không khớp sẽ <b>bị chặn</b>.</p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-2xs uppercase text-secondary-400 border-b border-secondary-100">
                    <th className="px-3 py-2">Vai trò SSO (công ty)</th>
                    <th className="px-3 py-2">→ Vai trò nội bộ</th>
                    <th className="px-3 py-2 text-center">Bật</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-50">
                  {maps.map((m) => (
                    <tr key={m.id} className="hover:bg-secondary-50/50">
                      <td className="px-3 py-2 font-mono text-secondary-800">{m.sso_role}</td>
                      <td className="px-3 py-2">
                        <select className="select w-auto text-sm" value={m.internal_role} onChange={(e) => changeMap(m, { internal_role: e.target.value })}>
                          {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                        </select>
                      </td>
                      <td className="px-3 py-2 text-center">
                        <input type="checkbox" checked={m.is_active} onChange={(e) => changeMap(m, { is_active: e.target.checked })} />
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => delMap(m)} className="text-secondary-300 hover:text-red-600" title="Xóa"><HiOutlineTrash className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {maps.length === 0 && <tr><td colSpan={4} className="px-3 py-4 text-center text-secondary-400">Chưa có ánh xạ nào.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-secondary-50 pt-4">
              <div>
                <label className="label">Vai trò SSO</label>
                <input className="input w-56" placeholder="vd: hcns_manager" value={newRole} onChange={(e) => setNewRole(e.target.value)} />
              </div>
              <div>
                <label className="label">→ Vai trò nội bộ</label>
                <select className="select w-auto" value={newInternal} onChange={(e) => setNewInternal(e.target.value)}>
                  {roles.map((r) => <option key={r} value={r}>{ROLE_LABELS[r] || r}</option>)}
                </select>
              </div>
              <button onClick={addMap} className="btn-primary"><HiOutlinePlus className="w-4 h-4" /> Thêm</button>
            </div>
          </div>

          {/* Liên kết tài khoản */}
          <div className="card p-6">
            <h2 className="text-lg font-semibold text-secondary-900 flex items-center gap-2 mb-1"><HiOutlineLink className="w-5 h-5 text-primary-600" /> Liên kết tài khoản</h2>
            <p className="text-sm text-secondary-500 mb-4">Gán <b>email công ty (SSO)</b> vào <b>tài khoản có sẵn</b> — để giữ dữ liệu cũ khi email lệch nhau. Không liên kết thì hệ thống tự khớp theo email trùng, không có thì tạo mới.</p>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-2xs uppercase text-secondary-400 border-b border-secondary-100">
                    <th className="px-3 py-2">Email công ty (SSO)</th>
                    <th className="px-3 py-2">→ Tài khoản nội bộ</th>
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-secondary-50">
                  {links.map((l) => (
                    <tr key={l.id} className="hover:bg-secondary-50/50">
                      <td className="px-3 py-2 text-secondary-800">{l.sso_email}</td>
                      <td className="px-3 py-2">
                        {l.user
                          ? <span>{l.user.full_name} <span className="text-secondary-400">({l.user.username} · {ROLE_LABELS[l.user.role] || l.user.role})</span></span>
                          : <span className="text-red-500">Tài khoản đã bị xóa</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => delLink(l)} className="text-secondary-300 hover:text-red-600" title="Gỡ liên kết"><HiOutlineTrash className="w-4 h-4" /></button>
                      </td>
                    </tr>
                  ))}
                  {links.length === 0 && <tr><td colSpan={3} className="px-3 py-4 text-center text-secondary-400">Chưa có liên kết nào.</td></tr>}
                </tbody>
              </table>
            </div>

            <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-secondary-50 pt-4">
              <div>
                <label className="label">Email công ty (SSO)</label>
                <input className="input w-64" placeholder="vd: nguyenvana@datxanhmientrung.com" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              </div>
              <div>
                <label className="label">→ Tài khoản nội bộ</label>
                <select className="select w-auto min-w-[200px]" value={newUserId} onChange={(e) => setNewUserId(e.target.value)}>
                  <option value="">— Chọn tài khoản —</option>
                  {users.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.username})</option>)}
                </select>
              </div>
              <button onClick={addLink} className="btn-primary"><HiOutlinePlus className="w-4 h-4" /> Liên kết</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default SsoConfigManagement
